import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Send, PenLine, Printer, Copy, XCircle, Link as LinkIcon, Save, Pencil, FileDown, Eye } from "lucide-react";
import {
  getContract, updateContract, sendContract, signAsFounder, terminateContract, getSignedPdfUrl,
} from "@/lib/contracts.functions";
import { ContractBuilder, ContractPreview, emptyContract, type ContractFormState } from "@/components/admin/ContractBuilder";
import { validateHourlyRateCurrency } from "@/lib/contract-currency-guard";

export const Route = createFileRoute("/admin_/contracts/$id")({
  ssr: false,
  head: () => ({ meta: [{ title: "Contract — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  beforeLoad: requireAdmin,
  component: ContractDetail,
});

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-muted text-foreground",
  pending_signature: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  expiring_soon: "bg-orange-100 text-orange-800",
  expired: "bg-red-100 text-red-800",
  cancelled: "bg-muted text-foreground",
  terminated: "bg-red-200 text-red-900",
};
const STATUS_LABELS: Record<string, string> = {
  draft: "Draft", pending_signature: "Pending Signature", active: "Active",
  expiring_soon: "Expiring Soon", expired: "Expired", cancelled: "Cancelled / Declined", terminated: "Terminated",
};

function ContractDetail() {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const get = useServerFn(getContract);
  const upd = useServerFn(updateContract);
  const send = useServerFn(sendContract);
  const sign = useServerFn(signAsFounder);
  const term = useServerFn(terminateContract);
  const pdfUrl = useServerFn(getSignedPdfUrl);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["contract", id],
    queryFn: () => get({ data: { id } }),
  });

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<ContractFormState>(emptyContract);
  const [signerName, setSignerName] = useState("Jana Burkhardt");
  const [signatureText, setSignatureText] = useState("Jana Burkhardt");
  const [showSign, setShowSign] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sendExpiryDays, setSendExpiryDays] = useState(30);
  const [sendByEmail, setSendByEmail] = useState(true);
  const [terminateReason, setTerminateReason] = useState("");
  const [terminateKind, setTerminateKind] = useState<"terminated" | "cancelled">("terminated");

  useEffect(() => {
    if (data?.contract) {
      const c = data.contract;
      setValue({
        title: c.title, type: c.type,
        counterparty_name: c.counterparty_name, counterparty_email: c.counterparty_email ?? "",
        counterparty_role: c.counterparty_role ?? "", counterparty_company: c.counterparty_company ?? "",
        company_name: c.company_name, start_date: c.start_date ?? "", end_date: c.end_date ?? "",
        notice_period: c.notice_period ?? "", payment_terms: c.payment_terms ?? "",
        payment_frequency: c.payment_frequency ?? "", hourly_rate: c.hourly_rate?.toString() ?? "",
        hourly_rate_currency: c.hourly_rate_currency ?? "GBP",
        revenue_share: Array.isArray(c.revenue_share) ? c.revenue_share as any : [],
        governing_law: c.governing_law ?? "", notes: c.notes ?? "",
        body: Array.isArray(c.body) ? c.body as any : [],
        custom_sections: Array.isArray(c.custom_sections) ? c.custom_sections as any : [],
      });
    }
  }, [data?.contract]);

  const saveMut = useMutation({
    mutationFn: () => upd({ data: {
      id, changeSummary: "Edited via builder",
      changes: {
        title: value.title, type: value.type as any,
        counterparty_name: value.counterparty_name,
        counterparty_email: value.counterparty_email || null,
        counterparty_role: value.counterparty_role || null,
        counterparty_company: value.counterparty_company || null,
        company_name: value.company_name,
        start_date: value.start_date || null,
        end_date: value.end_date || null,
        notice_period: value.notice_period || null,
        payment_terms: value.payment_terms || null,
        payment_frequency: value.payment_frequency || null,
        hourly_rate: value.hourly_rate ? parseFloat(value.hourly_rate) : null,
        hourly_rate_currency: "USD",
        revenue_share: value.revenue_share,
        governing_law: value.governing_law || null,
        notes: value.notes || null,
        body: value.body,
        custom_sections: value.custom_sections,
      },
    } }),
    onSuccess: () => { toast.success("Saved"); setEditing(false); qc.invalidateQueries({ queryKey: ["contract", id] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const sendMut = useMutation({
    mutationFn: (opts: { expiresInDays: number; sendEmail: boolean }) => send({ data: { id, ...opts } }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["contract", id] });
      setShowSend(false);
      if (res?.email?.ok) {
        toast.success("Contract sent to counterparty by email");
      } else if (res?.email && !res.email.ok) {
        toast.error(`Contract prepared, but email send failed (${res.email.status})`);
      } else {
        toast.success("Contract marked as sent");
      }
      if (res?.signing_token) {
        const url = `${window.location.origin}/sign/contract/${res.signing_token}`;
        void navigator.clipboard?.writeText(url);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadPdf = async () => {
    try {
      const res = await pdfUrl({ data: { id } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e: any) { toast.error(e.message); }
  };

  const signMut = useMutation({
    mutationFn: () => sign({ data: { id, signerName, signatureText } }),
    onSuccess: () => { toast.success("Signed"); setShowSign(false); qc.invalidateQueries({ queryKey: ["contract", id] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const termMut = useMutation({
    mutationFn: () => term({ data: { id, reason: terminateReason, kind: terminateKind } }),
    onSuccess: () => { toast.success("Contract updated"); setShowTerminate(false); qc.invalidateQueries({ queryKey: ["contract", id] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;
  if (error || !data?.contract) return <div className="p-8 text-center text-sm text-destructive">Contract not found.</div>;

  const c = data.contract;
  const founderSigned = data.signatures.some((s: any) => s.signer_type === "founder");
  const counterSigned = data.signatures.some((s: any) => s.signer_type === "counterparty");
  const signingUrl = c.signing_token ? `${typeof window !== "undefined" ? window.location.origin : ""}/sign/contract/${c.signing_token}` : "";

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/admin/contracts" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground print:hidden">
          <ArrowLeft className="h-4 w-4" /> Back to contracts
        </Link>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4 print:hidden">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[c.status] ?? ""}`}>
                {STATUS_LABELS[c.status] ?? c.status}
              </span>
              {c.type && <span className="text-xs text-muted-foreground">v{c.version}</span>}
            </div>
            <h1 className="font-display text-3xl text-foreground">{c.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {c.counterparty_name}{c.counterparty_role ? ` · ${c.counterparty_role}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <button onClick={() => { const err = validateHourlyRateCurrency(value.hourly_rate_currency); if (err) { toast.error(err); return; } saveMut.mutate(); }} disabled={saveMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
                  <Save className="h-4 w-4" /> {saveMut.isPending ? "Saving…" : "Save changes"}
                </button>
                <button onClick={() => setEditing(false)} className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                {(c.status === "draft" || c.status === "pending_signature") && (
                  <button onClick={() => setShowSend(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    <Send className="h-4 w-4" /> {c.status === "pending_signature" ? "Resend" : "Send for signing"}
                  </button>
                )}
                {c.signed_pdf_path && (
                  <button onClick={downloadPdf} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100">
                    <FileDown className="h-4 w-4" /> Download signed PDF
                  </button>
                )}
                {!founderSigned && c.status !== "cancelled" && c.status !== "terminated" && (
                  <button onClick={() => setShowSign(true)} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100">
                    <PenLine className="h-4 w-4" /> Sign as founder
                  </button>
                )}
                <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                  <Printer className="h-4 w-4" /> Print / PDF
                </button>
                {(c.status === "active" || c.status === "pending_signature" || c.status === "expiring_soon") && (
                  <button onClick={() => setShowTerminate(true)} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                    <XCircle className="h-4 w-4" /> Terminate
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {c.status === "pending_signature" && signingUrl && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm print:hidden">
            <p className="mb-2 font-medium text-amber-900">
              Signing link (share with counterparty)
              {c.sign_expires_at && <span className="ms-2 text-xs font-normal text-amber-800">· expires {new Date(c.sign_expires_at).toLocaleString()}</span>}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-card px-2 py-1 text-xs text-amber-900 dark:text-amber-200">{signingUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(signingUrl); toast.success("Copied"); }} className="inline-flex items-center gap-1 rounded border border-amber-300 bg-card px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-400/40 dark:text-amber-200 dark:hover:bg-amber-500/15">
                <Copy className="h-3 w-3" /> Copy
              </button>
              <a href={signingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-amber-300 bg-card px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-400/40 dark:text-amber-200 dark:hover:bg-amber-500/15">
                <LinkIcon className="h-3 w-3" /> Open
              </a>
            </div>
            {c.viewed_at && (
              <p className="mt-2 flex items-center gap-1 text-xs text-amber-800"><Eye className="h-3 w-3" /> Counterparty viewed {new Date(c.viewed_at).toLocaleString()}</p>
            )}
          </div>
        )}

        {editing ? (
          <ContractBuilder value={value} onChange={setValue} />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div>
              <ContractPreview value={value} signatures={data.signatures as any} />
            </div>
            <aside className="space-y-4 print:hidden">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-3 font-display">Signatures</h3>
                {data.signatures.length === 0 && <p className="text-sm text-muted-foreground">No signatures yet.</p>}
                <ul className="space-y-2">
                  {data.signatures.map((s: any) => (
                    <li key={s.id} className="text-sm">
                      <span className="font-medium">{s.signer_type === "founder" ? "Founder" : "Counterparty"}:</span> {s.signer_name}
                      <div className="text-xs text-muted-foreground">
                        {new Date(s.signed_at).toLocaleString()}{s.ip_address ? ` · IP ${s.ip_address}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-3 font-display">Version history</h3>
                {data.versions.length === 0 && <p className="text-sm text-muted-foreground">Current version {c.version}.</p>}
                <ul className="space-y-1 text-sm">
                  {data.versions.map((v: any) => (
                    <li key={v.id} className="text-muted-foreground">
                      v{v.version_number} · {new Date(v.edited_at).toLocaleString()}
                      {v.change_summary ? ` — ${v.change_summary}` : ""}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-3 font-display">Audit log</h3>
                <ul className="space-y-1 text-xs">
                  {data.audit.map((a: any) => (
                    <li key={a.id} className="text-muted-foreground">
                      <span className="font-medium text-foreground">{a.action}</span> — {new Date(a.created_at).toLocaleString()}
                      {a.actor_email ? ` · ${a.actor_email}` : ""}
                    </li>
                  ))}
                </ul>
              </div>

              {c.notes && (
                <div className="rounded-xl border border-border bg-card p-5">
                  <h3 className="mb-2 font-display">Internal notes</h3>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{c.notes}</p>
                </div>
              )}
            </aside>
          </div>
        )}

        {showSign && (
          <Modal onClose={() => setShowSign(false)} title="Sign contract as founder">
            <p className="mb-3 text-sm text-muted-foreground">
              By typing your name below and clicking Sign, you are electronically signing this contract. Your typed name, timestamp, and IP address will be recorded.
            </p>
            <label className="mb-3 block text-sm">Full name
              <input className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
            </label>
            <label className="mb-4 block text-sm">Typed signature
              <input className="mt-1 w-full rounded border border-border bg-background px-3 py-2 font-signature text-lg italic" value={signatureText} onChange={(e) => setSignatureText(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSign(false)} className="rounded border border-border px-3 py-2 text-sm">Cancel</button>
              <button onClick={() => signMut.mutate()} disabled={signMut.isPending} className="rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
                {signMut.isPending ? "Signing…" : "Sign"}
              </button>
            </div>
          </Modal>
        )}

        {showTerminate && (
          <Modal onClose={() => setShowTerminate(false)} title="Terminate or cancel contract">
            <label className="mb-3 block text-sm">Action
              <select value={terminateKind} onChange={(e) => setTerminateKind(e.target.value as any)} className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm">
                <option value="terminated">Terminate (end an active contract)</option>
                <option value="cancelled">Cancel (before activation)</option>
              </select>
            </label>
            <label className="mb-4 block text-sm">Reason
              <textarea className="mt-1 w-full min-h-[80px] rounded border border-border bg-background px-3 py-2 text-sm" value={terminateReason} onChange={(e) => setTerminateReason(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowTerminate(false)} className="rounded border border-border px-3 py-2 text-sm">Cancel</button>
              <button onClick={() => termMut.mutate()} disabled={termMut.isPending} className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
                {termMut.isPending ? "Working…" : "Confirm"}
              </button>
            </div>
          </Modal>
        )}

        {showSend && (
          <Modal onClose={() => setShowSend(false)} title="Send contract for signing">
            {c.counterparty_email ? (
              <p className="mb-3 text-sm text-muted-foreground">
                Send a secure signing link to <strong>{c.counterparty_email}</strong>.
              </p>
            ) : (
              <p className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
                No counterparty email on file. Save one first, or use the signing link that will be generated to share manually.
              </p>
            )}
            <label className="mb-3 block text-sm">Signing expires after
              <select value={sendExpiryDays} onChange={(e) => setSendExpiryDays(parseInt(e.target.value))} className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm">
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </label>
            <label className="mb-4 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sendByEmail && !!c.counterparty_email} disabled={!c.counterparty_email} onChange={(e) => setSendByEmail(e.target.checked)} />
              <span>Email the signing link to the counterparty from info@recoverytrauma.com</span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSend(false)} className="rounded border border-border px-3 py-2 text-sm">Cancel</button>
              <button
                onClick={() => sendMut.mutate({ expiresInDays: sendExpiryDays, sendEmail: sendByEmail && !!c.counterparty_email })}
                disabled={sendMut.isPending}
                className="rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {sendMut.isPending ? "Sending…" : "Send"}
              </button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
        <h2 className="mb-4 font-display text-lg">{title}</h2>
        {children}
      </div>
    </div>
  );
}
